
import { defineComponent, computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/store/api.service";
import {
  FETCH_APPS,
  SET_SELECTED_APP,
  SET_SELECTED_ROLE,
} from "@/store/appSelector.module";
import type { App, Role } from "@/store/appSelector.module";
import { FETCH_JWT, LOGOUT } from "@/store/auth.module";

import IconDashboard from "@/assets/image/IconDashboard.vue";
import IconGlobe from "@/assets/image/IconGlobe.vue";
import IconFinance from "@/assets/image/IconFinance.vue";
import IconLogout from "@/assets/image/IconLogout.vue";
import { ROLE_DASHBOARD_TITLES } from "@/utils/permissions";

import "@/assets/css/AppSelector.css";

export default defineComponent({
  name: "AppSelectorView",
  components: { IconLogout },

  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const isEntering = ref(false);
    const enterError = ref<string | null>(null);
    const showLogoutConfirm = ref(false);

    const iconMap: Record<string, any> = {
      dashboard: IconDashboard,
      globe: IconGlobe,
      finance: IconFinance,
    };

    const apps = computed(() => store.getters["appSelector/allApps"]);
    const selectedApp = computed(() => store.getters["appSelector/selectedApp"]);
    const selectedRole = computed(() => store.getters["appSelector/selectedRole"]);
    const loading = computed(() => store.getters["appSelector/appsLoading"]);
    const error = computed(() => store.getters["appSelector/appsError"]);
    const canEnter = computed(() => store.getters["appSelector/canEnter"]);
    const currentUser = computed(() => store.getters.currentUser);

    const originAppId = computed(() => (route.query.from as string) || null);
    const accessWarning = computed(() => {
      if (route.query.reason === "no-web-access") {
        return "Akun Anda tidak memiliki akses ke modul web admin. Pilih modul lain yang tersedia.";
      }

      return null;
    });

    const getWebRoleTitle = (roleId?: string | null) => (
      roleId ? ROLE_DASHBOARD_TITLES[roleId] : undefined
    );

    const getAppDisplayName = (app: App) => {
      if (app.id !== "web") return app.name;

      if (selectedApp.value?.id === app.id) {
        return getWebRoleTitle(selectedRole.value?.id) || app.name;
      }

      if (app.roles.length === 1) {
        return getWebRoleTitle(app.roles[0].id) || app.name;
      }

      return app.name;
    };

    const getRoleDisplayName = (role: Role) => {
      if (selectedApp.value?.id !== "web") return role.name;
      return getWebRoleTitle(role.id) || role.name;
    };

    const loadApps = async () => {
      await store.dispatch(FETCH_JWT);
      await store.dispatch(`appSelector/${FETCH_APPS}`);

      if (originAppId.value) {
        const found = apps.value.find((a: App) => a.id === originAppId.value);
        if (found) {
          await store.dispatch(`appSelector/${SET_SELECTED_APP}`, found);
          if (found.roles.length === 1) {
            await store.dispatch(`appSelector/${SET_SELECTED_ROLE}`, found.roles[0]);
          }
        }
      }
    };

    onMounted(loadApps);

    const selectApp = (app: App) => {
      store.dispatch(`appSelector/${SET_SELECTED_APP}`, app);
    };

    const selectRole = (role: Role) => {
      store.dispatch(`appSelector/${SET_SELECTED_ROLE}`, role);
    };

    const handleEnter = async () => {
      if (!canEnter.value || isEntering.value) return;
      isEntering.value = true;
      enterError.value = null;

      try {
        const app = selectedApp.value!;
        const role = selectedRole.value!;

        const response: any = await ApiService.post("/auth/select", {
          appId: app.id,
          role: role.id,
        });

        const payload = response?.data || response;
        const redirectUrl = payload?.redirectUrl || app.url;

        if (redirectUrl.startsWith("/")) {
          await router.push(redirectUrl);
        } else {
          window.location.href = redirectUrl;
        }
      } catch (err: any) {
        enterError.value = err?.message || 'Gagal masuk ke aplikasi. Coba lagi.';
      } finally {
        isEntering.value = false;
      }
    };

    const handleLogout = async () => {
      showLogoutConfirm.value = false;
      await store.dispatch(LOGOUT);
    };

    return {
      apps, selectedApp, selectedRole,
      loading, error, canEnter,
      currentUser, accessWarning,
      isEntering, enterError, showLogoutConfirm,
      originAppId, iconMap,
      getAppDisplayName, getRoleDisplayName,
      loadApps, selectApp, selectRole,
      handleEnter, handleLogout,
    };
  },
});
